// ============================================
// Video Room Component
// Main container for 100ms video calls
// ============================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  useHMSStore,
  useHMSActions,
  useHMSNotifications,
  HMSNotificationTypes,
  selectPeers,
  selectLocalPeer,
  selectIsConnectedToRoom,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectIsSomeoneScreenSharing,
  selectRoomState,
  selectConnectionQuality,
  HMSRoomState
} from '@100mslive/react-sdk';
import VideoGrid from './VideoGrid';
import VideoControls from './VideoControls';
import ScreenShareView from './ScreenShareView';

/**
 * Custom hook for playing a dialing tone while waiting for call recipient
 * Uses Web Audio API to generate a realistic phone ringing pattern
 */
const useDialingSound = (isDialing) => {
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isDialing) {
      // Stop all sounds
      oscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {
          // Oscillator may already be stopped
        }
      });
      oscillatorsRef.current = [];
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      return;
    }

    // Create audio context
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      console.warn('Web Audio API not supported');
      return;
    }

    audioContextRef.current = new AudioContext();
    const ctx = audioContextRef.current;

    // Function to play a single ring (two-tone burst)
    const playRing = () => {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') return;

      const ctx = audioContextRef.current;
      const now = ctx.currentTime;

      // Create oscillators for dual-tone (440Hz + 480Hz is standard US dial tone)
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc1.type = 'sine';
      osc2.type = 'sine';
      osc1.frequency.setValueAtTime(440, now);
      osc2.frequency.setValueAtTime(480, now);

      // Set volume and fade
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.15, now + 0.05);
      gainNode.gain.setValueAtTime(0.15, now + 0.4);
      gainNode.gain.linearRampToValueAtTime(0, now + 0.5);

      osc1.connect(gainNode);
      osc2.connect(gainNode);
      gainNode.connect(ctx.destination);

      osc1.start(now);
      osc2.start(now);
      osc1.stop(now + 0.5);
      osc2.stop(now + 0.5);

      oscillatorsRef.current.push(osc1, osc2);

      // Clean up stopped oscillators
      setTimeout(() => {
        oscillatorsRef.current = oscillatorsRef.current.filter(
          o => o !== osc1 && o !== osc2
        );
      }, 600);
    };

    // Resume audio context (required for user interaction policy)
    ctx.resume().then(() => {
      // Play first ring immediately
      playRing();
      // Then ring every 2 seconds (standard ring cadence)
      intervalRef.current = setInterval(playRing, 2000);
    }).catch(err => {
      console.warn('Could not resume audio context:', err);
    });

    // Cleanup
    return () => {
      oscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {}
      });
      oscillatorsRef.current = [];
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, [isDialing]);
};

/**
 * Inner video room content - uses HMS hooks
 */
const VideoRoomContent = ({ roomId, roomType, onLeave, onEnd, onError, calleeName }) => {
  const hmsActions = useHMSActions();
  const notification = useHMSNotifications();
  const peers = useHMSStore(selectPeers);
  const localPeer = useHMSStore(selectLocalPeer);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const isAudioEnabled = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoEnabled = useHMSStore(selectIsLocalVideoEnabled);
  const isSomeoneScreenSharing = useHMSStore(selectIsSomeoneScreenSharing);
  const roomState = useHMSStore(selectRoomState);

  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [error, setError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);

  // Check if we're waiting for the other person (caller waiting for recipient)
  const isWaitingForRecipient = roomType === 'dm_call' && peers.length === 1;

  // Play dialing sound while waiting for recipient
  useDialingSound(isWaitingForRecipient);

  // Debug room state
  useEffect(() => {
    console.log('Room state changed:', roomState, 'isConnected:', isConnected, 'peers:', peers.length);
  }, [roomState, isConnected, peers.length]);

  // Handle HMS notifications (including errors)
  useEffect(() => {
    if (!notification) return;

    console.log('HMS Notification:', notification.type, notification);

    switch (notification.type) {
      case HMSNotificationTypes.ERROR:
        console.error('HMS Error:', notification.data);
        setError(notification.data?.message || 'Connection error');
        onError?.(notification.data?.message);
        break;
      case HMSNotificationTypes.RECONNECTING:
        console.log('HMS Reconnecting...');
        break;
      case HMSNotificationTypes.RECONNECTED:
        console.log('HMS Reconnected');
        break;
      default:
        break;
    }
  }, [notification, onError]);

  // Toggle audio
  const toggleAudio = useCallback(async () => {
    try {
      await hmsActions.setLocalAudioEnabled(!isAudioEnabled);
    } catch (err) {
      console.error('Error toggling audio:', err);
    }
  }, [hmsActions, isAudioEnabled]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    try {
      await hmsActions.setLocalVideoEnabled(!isVideoEnabled);
    } catch (err) {
      console.error('Error toggling video:', err);
    }
  }, [hmsActions, isVideoEnabled]);

  // Toggle screen share
  const toggleScreenShare = useCallback(async () => {
    try {
      if (isScreenSharing) {
        await hmsActions.setScreenShareEnabled(false);
      } else {
        await hmsActions.setScreenShareEnabled(true);
      }
      setIsScreenSharing(!isScreenSharing);
    } catch (err) {
      console.error('Error toggling screen share:', err);
      if (err.message?.includes('Permission denied')) {
        setError('Screen sharing permission denied');
      }
    }
  }, [hmsActions, isScreenSharing]);

  // Leave room
  const leaveRoom = useCallback(async () => {
    try {
      await hmsActions.leave();
      onLeave?.();
    } catch (err) {
      console.error('Error leaving room:', err);
      // Still call onLeave even if there's an error
      onLeave?.();
    }
  }, [hmsActions, onLeave]);

  // End room for everyone (host only)
  const endRoom = useCallback(async () => {
    try {
      // Leave the HMS room
      await hmsActions.leave();
      // Call the onEnd handler to notify server
      await onEnd?.();
    } catch (err) {
      console.error('Error ending room:', err);
      // Still call onEnd even if there's an error
      onEnd?.();
    }
  }, [hmsActions, onEnd]);

  // Handle errors
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Loading state
  if (!isConnected) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
        <p className="text-white text-lg">Connecting to video call...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we set up your call</p>
        <p className="text-gray-500 text-xs mt-4">Room state: {roomState}</p>
        {error && (
          <div className="mt-4 p-3 bg-red-900/50 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        <button
          onClick={onLeave}
          className="mt-6 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Error message */}
      {error && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}

      {/* Recording indicator */}
      {isRecording && (
        <div className="absolute top-4 right-4 z-50 flex items-center space-x-2 bg-red-600 text-white px-3 py-1.5 rounded-full">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          <span className="text-sm font-medium">Recording</span>
        </div>
      )}

      {/* Calling/Ringing indicator - shown when waiting for recipient */}
      {isWaitingForRecipient && (
        <div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-gray-900/80">
          {/* Pulsing phone icon */}
          <div className="relative mb-6">
            <div className="absolute inset-0 rounded-full bg-green-500/30 animate-ping"></div>
            <div className="relative w-20 h-20 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
          </div>

          {/* Calling text */}
          <h2 className="text-2xl font-semibold text-white mb-2">
            Calling{calleeName ? ` ${calleeName}` : ''}...
          </h2>
          <p className="text-gray-400 mb-8">Waiting for them to answer</p>

          {/* Animated dots */}
          <div className="flex space-x-2 mb-8">
            <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
            <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
            <span className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
          </div>

          {/* Cancel button */}
          <button
            onClick={onLeave}
            className="px-6 py-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Cancel Call</span>
          </button>
        </div>
      )}

      {/* Room type badge */}
      <div className="absolute top-4 left-4 z-50">
        <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
          roomType === 'interview' ? 'bg-blue-600 text-white' :
          roomType === 'channel_huddle' ? 'bg-green-600 text-white' :
          'bg-gray-700 text-white'
        }`}>
          {roomType === 'interview' ? 'Interview' :
           roomType === 'channel_huddle' ? 'Channel Huddle' :
           'Video Call'}
        </span>
      </div>

      {/* Participant count */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
        <span className="bg-gray-800 text-white px-3 py-1.5 rounded-full text-sm">
          {peers.length} participant{peers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Main Video Area */}
      <div className="flex-1 relative overflow-hidden">
        {isSomeoneScreenSharing ? (
          <ScreenShareView peers={peers} localPeer={localPeer} />
        ) : (
          <VideoGrid peers={peers} localPeer={localPeer} />
        )}
      </div>

      {/* Controls */}
      <VideoControls
        isAudioEnabled={isAudioEnabled}
        isVideoEnabled={isVideoEnabled}
        isScreenSharing={isScreenSharing}
        onToggleAudio={toggleAudio}
        onToggleVideo={toggleVideo}
        onToggleScreenShare={toggleScreenShare}
        onLeave={leaveRoom}
        onEnd={onEnd ? endRoom : null}
        roomType={roomType}
      />
    </div>
  );
};

/**
 * Video Room wrapper with HMS Provider
 */
const VideoRoom = ({ roomId, token, roomType = 'dm_call', onLeave, onEnd, onError, userName, calleeName }) => {
  const hmsActions = useHMSActions();
  const notification = useHMSNotifications();
  const roomState = useHMSStore(selectRoomState);
  const [joinError, setJoinError] = useState(null);
  const [isJoining, setIsJoining] = useState(true);
  const hasJoinedRef = React.useRef(false);
  const isMountedRef = React.useRef(true);

  // Listen for errors during join
  useEffect(() => {
    if (!notification) return;

    if (notification.type === HMSNotificationTypes.ERROR) {
      console.error('HMS Error during join:', notification.data);
      setJoinError(notification.data?.message || 'Connection error');
      setIsJoining(false);
    }
  }, [notification]);

  // Monitor room state
  useEffect(() => {
    console.log('VideoRoom roomState:', roomState);
    if (roomState === HMSRoomState.Connected) {
      setIsJoining(false);
    } else if (roomState === HMSRoomState.Failed) {
      setJoinError('Failed to connect to room');
      setIsJoining(false);
    }
  }, [roomState]);

  useEffect(() => {
    isMountedRef.current = true;

    // Prevent double-joining
    if (hasJoinedRef.current) {
      console.log('Already joined, skipping...');
      return;
    }

    const joinRoom = async () => {
      if (!token) {
        setJoinError('No video token provided');
        setIsJoining(false);
        onError?.('No video token provided');
        return;
      }

      try {
        hasJoinedRef.current = true;
        setIsJoining(true);
        console.log('Attempting to join 100ms room with token:', token.substring(0, 20) + '...');
        console.log('Full token length:', token.length);
        await hmsActions.join({
          authToken: token,
          userName: userName || 'User',
          settings: {
            isAudioMuted: false,
            isVideoMuted: false
          },
          rememberDeviceSelection: true
        });
        console.log('hmsActions.join() completed');
        // Don't set isJoining to false here - let roomState change handle it
      } catch (err) {
        console.error('Error joining room:', err);
        hasJoinedRef.current = false; // Allow retry on error
        setJoinError(err.message || 'Failed to join video call');
        setIsJoining(false);
        onError?.(err.message);
      }
    };

    joinRoom();

    // Cleanup - but delay to handle React StrictMode double-mount
    return () => {
      isMountedRef.current = false;
      // Use timeout to check if this is a real unmount or StrictMode fake unmount
      setTimeout(() => {
        if (!isMountedRef.current) {
          console.log('VideoRoom truly unmounting, leaving room...');
          hmsActions.leave().catch(console.error);
          hasJoinedRef.current = false;
        }
      }, 100);
    };
  }, [token]); // Only depend on token

  if (joinError) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900">
        <div className="text-red-500 text-lg mb-4">Failed to join video call</div>
        <p className="text-gray-400 mb-4">{joinError}</p>
        <button
          onClick={onLeave}
          className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (isJoining) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
        <p className="text-white text-lg">Joining video call...</p>
        <p className="text-gray-500 text-xs mt-4">Room state: {roomState}</p>
        <button
          onClick={onLeave}
          className="mt-6 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <VideoRoomContent
      roomId={roomId}
      roomType={roomType}
      onLeave={onLeave}
      onEnd={onEnd}
      onError={onError}
      calleeName={calleeName}
    />
  );
};

// HMSRoomProvider is now at the app level (AppRouter.js) to prevent
// React StrictMode from destroying WebRTC connections during double mount/unmount
export default VideoRoom;
